import { useState } from "react";
import {
  ComposedChart, Area, ReferenceArea, ReferenceLine, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import ProjectionAssumptions from "./ProjectionAssumptions";
import Spinner from "./Spinner";

const HEALTH_COLORS = {
  rupture: "#DC2626",
  critique: "#EF4444",
  fragile: "#D97706",
  sain: "#65A30D",
  confortable: "#059669",
  inconnu: "#94A3B8",
};

const HEALTH_LABELS = {
  rupture: "Rupture",
  critique: "Critique",
  fragile: "Fragile",
  sain: "Sain",
  confortable: "Confortable",
  inconnu: "Inconnu",
};

const eurK = (v) => (v == null ? "—" : `${Math.round(v / 1000).toLocaleString("fr-FR")} k€`);
const eur = (v) => (v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`);

const ProjectionTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const pt = payload[0]?.payload;
  if (!pt) return null;
  const color = HEALTH_COLORS[pt.health_status] || HEALTH_COLORS.inconnu;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 text-xs space-y-1.5" style={{ minWidth: 190 }}>
      <div className="flex items-center justify-between gap-4">
        <span className="font-semibold text-slate-900">{label}</span>
        <span
          className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ backgroundColor: `${color}1A`, color }}
        >
          {HEALTH_LABELS[pt.health_status] || pt.health_status}
        </span>
      </div>
      <p className="text-slate-500">Population : <span className="text-slate-700 font-medium">{Math.round(pt.population_total)}</span></p>
      <p className="text-slate-500">Décès attendus : <span className="text-slate-700 font-medium">{pt.expected_deaths?.toFixed(2) ?? "—"}</span></p>
      <p className="text-slate-500">Besoin en capital : <span className="text-slate-700 font-medium">{eur(pt.capital_need)}</span></p>
      <p className="text-slate-500">Fonds projeté : <span className="text-slate-700 font-medium">{eur(pt.fund_balance)}</span></p>
    </div>
  );
};

/* ── Carte pleine largeur : besoin en capital vs fonds projeté sur N ans,
 *    indicateur de santé pas à pas (fond du graphique) + panneau d'hypothèses. ── */
const CapitalProjection = ({
  loading, series, summary, dataQuality, assumptions, defaults, overrides, onAssumptionsChange,
}) => {
  const [panelOpen, setPanelOpen] = useState(false);

  const hasData = Array.isArray(series) && series.length > 0;
  const financialAvailable = hasData && series.some((pt) => pt.fund_balance != null);
  const hasNegativeFund = hasData && series.some((pt) => pt.fund_balance != null && pt.fund_balance < 0);
  const breachYear = summary?.breach_year;
  const horizonLabel = assumptions?.horizon_years ?? 30;

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-medium text-slate-800 text-sm">Projection du fonds — {horizonLabel} ans</h3>
          <p className="text-xs text-slate-400 mt-0.5">Besoin en capital (provision cible) vs fonds disponible projeté</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: "#D97706" }} />
            Besoin en capital
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: "#059669" }} />
            Fonds projeté
          </span>
        </div>
      </div>

      <ProjectionAssumptions
        open={panelOpen}
        onToggle={() => setPanelOpen((o) => !o)}
        assumptions={assumptions}
        defaults={defaults}
        overrides={overrides}
        onChange={onAssumptionsChange}
      />

      {loading && !hasData ? (
        <div className="flex items-center justify-center h-64"><Spinner /></div>
      ) : !financialAvailable ? (
        <div className="h-64 flex items-center justify-center text-center px-8">
          <p className="text-sm text-slate-400">
            Configurez le coût moyen de rapatriement pour activer la projection du fonds sur {horizonLabel} ans.
          </p>
        </div>
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={series} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              {series.map((pt, i) => (
                <ReferenceArea
                  key={pt.year}
                  x1={pt.year}
                  x2={i < series.length - 1 ? series[i + 1].year : pt.year + 1}
                  fill={HEALTH_COLORS[pt.health_status] || HEALTH_COLORS.inconnu}
                  fillOpacity={0.07}
                  stroke="none"
                  ifOverflow="visible"
                />
              ))}
              <XAxis
                dataKey="year"
                type="number"
                domain={["dataMin", "dataMax"]}
                tick={{ fontSize: 10, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={eurK}
                tick={{ fontSize: 10, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
                width={56}
              />
              <Tooltip content={<ProjectionTooltip />} />
              {hasNegativeFund && <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />}
              {breachYear != null && (
                <ReferenceLine
                  x={breachYear}
                  stroke="#DC2626"
                  strokeDasharray="4 4"
                  label={{ value: "rupture", position: "top", fontSize: 10, fill: "#DC2626" }}
                />
              )}
              <Area
                type="monotone"
                dataKey="capital_need"
                stroke="#D97706"
                strokeWidth={1.5}
                fill="#D97706"
                fillOpacity={0.12}
                connectNulls
              />
              <Area
                type="monotone"
                dataKey="fund_balance"
                stroke="#059669"
                strokeWidth={2.5}
                fill="#059669"
                fillOpacity={0.15}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-3 space-y-1 px-1">
        {dataQuality?.fund_baseline_is_contributions_only && (
          <p className="text-xs text-slate-400">
            Le solde de départ ne tient compte que des cotisations encaissées : les rapatriements déjà
            financés ne sont pas déduits (module sinistres à venir). Ajustez « Solde de départ » dans les
            hypothèses si vous connaissez la trésorerie réelle.
          </p>
        )}
        <p className="text-xs text-slate-400">
          Modèle démographique par cohortes, déterministe (pas de simulation aléatoire) — ordre de
          grandeur, pas un engagement.
        </p>
      </div>
    </div>
  );
};

export default CapitalProjection;
